import { useRef, useEffect } from "react";
import { Notification } from "@/services/notification-services";
import { NotificationItem } from "./NotificationItem";

interface NotificationDrawerProps {
  notifications: Notification[];
  isLoading: boolean;
  unreadCount: number;
  onRead: (id: number) => void;
  onMarkAllRead: () => void;
  onClose: () => void;
}

export function NotificationDrawer({
  notifications,
  isLoading,
  unreadCount,
  onRead,
  onMarkAllRead,
  onClose,
}: NotificationDrawerProps) {
  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <div
      ref={drawerRef}
      role="dialog"
      aria-label="Notifications"
      className="absolute right-0 top-full mt-2 w-80 sm:w-96 rounded-xl shadow-xl border border-light-border dark:border-dark-border bg-light-background dark:bg-dark-inputbg overflow-hidden z-50"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-light-border dark:border-dark-border">
        <h2 className="text-sm font-semibold text-light dark:text-dark">Notifications</h2>
        {unreadCount > 0 && (
          <button
            onClick={onMarkAllRead}
            className="text-xs text-light-primary dark:text-dark-primary hover:opacity-75 transition-opacity font-medium"
          >
            Mark all as read
          </button>
        )}
      </div>

      {/* Content */}
      <div className="overflow-y-auto max-h-[70vh]">
        {isLoading ? (
          <div className="flex flex-col gap-3 p-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-start gap-3 animate-pulse">
                <div className="w-8 h-8 rounded-full bg-light-muted/50 dark:bg-dark-surface/50 flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 bg-light-muted/50 dark:bg-dark-surface/50 rounded w-full" />
                  <div className="h-3 bg-light-muted/50 dark:bg-dark-surface/50 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div className="w-12 h-12 rounded-full bg-light-muted/40 dark:bg-dark-surface/40 flex items-center justify-center mb-3">
              <span className="text-xl">🔔</span>
            </div>
            <p className="text-sm font-medium text-light/70 dark:text-dark/70">No notifications yet</p>
            <p className="text-xs text-light/50 dark:text-dark/50 mt-1">
              You'll be notified when your trainer leaves a note
            </p>
          </div>
        ) : (
          <div className="divide-y divide-light-border dark:divide-dark-border">
            {notifications.map((n) => (
              <NotificationItem key={n.id} notification={n} onRead={onRead} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
